import moment from "moment-timezone";

import makePropsEnumerable from "../utility/MakePropsEnumerable";
import EventFilterNames from "./EventFilterNames";

const mEventNameFilter = new WeakMap();
const mEventRangeStart = new WeakMap();
const mEventRangeEnd = new WeakMap();
const mEventReportedRangeStart = new WeakMap();
const mEventReportedRangeEnd = new WeakMap();
const mIncludeEventTypeFilter = new WeakMap();
const mExcludeEventTypeFilter = new WeakMap();
const mIncludeStatusFilter = new WeakMap();
const mExcludeStatusFilter = new WeakMap();
const mEventTrackerFilter = new WeakMap();
const mEventHasNoTrackersFilter = new WeakMap();
const mTargetFilter = new WeakMap();
const mTargetFilterRequireAll = new WeakMap();
const mLocationStateFilter = new WeakMap();
const mOfficeStateFilter = new WeakMap();
const mOfficeTypeFilter = new WeakMap();


function isNull(checkVal, replaceVal) {
    return ((checkVal != null) ? checkVal : replaceVal);
}

function readRangeParamFromStr(isoFormatStr) {
    let rangeParam = null;

    if (typeof isoFormatStr === "string") {
        rangeParam = moment(isoFormatStr, moment.ISO_8601, true);
    }

    if (rangeParam == null) {
        return null;
    }
    else if (rangeParam.isValid()) {
        return rangeParam;
    }
    else {
        return null;
    }
}

function buildObjForIdParam(idParamStr, filterFieldName) {
    let idParam = parseInt(idParamStr, 10);

    if (Number.isInteger(idParam)) {
        return {
            field: filterFieldName,
            operator: "eq",
            value: idParam
        };
    }
    else {
        return null;
    }
}

function clearAndReplaceArray(source, target) {
    if (source == null) {
        target = [];
    }
    else {
        target.length = 0;
        Array.prototype.push.apply(target, source);
    }
}

function normalizeFilterEntry(filterEntry) {

    //pseudo-code:
    //remove the last two entries and store them in a 2-tuple.
    //create a new filter object with the current filter entry logic block
    //copy the elements of 2-tuple to the new filter object's filters array.
    let normalizedFilterEntry = angular.copy(filterEntry);
    while (normalizedFilterEntry.filters.length > 2) {
        let subFilterObj = {
            "logic": normalizedFilterEntry.logic,
            "filters": []
        };

        subFilterObj.filters.unshift(normalizedFilterEntry.filters.pop());
        subFilterObj.filters.unshift(normalizedFilterEntry.filters.pop());

        normalizedFilterEntry.filters.push(subFilterObj);
    }

    return normalizedFilterEntry;
}

class EventFilter {
    constructor({
        eventNameFilter = "",
        includeEventTypeFilter = [],
        excludeEventTypeFilter =[],
        includeStatusFilter = [],
        excludeStatusFilter = [],
        eventTrackerFilter = [],
        eventHasNoTrackersFilter = false,
        targetFilter = [],
        targetFilterRequireAll = false,
        locationStateFilter = [],
        officeStateFilter = [],
        officeTypeFilter = [],
        eventRangeStart = null,
        eventRangeEnd = null,
        eventReportedRangeStart = null,
        eventReportedRangeEnd = null
    }) {
        mEventNameFilter.set(this, isNull(eventNameFilter, ""));
        mIncludeEventTypeFilter.set(this, isNull(includeEventTypeFilter, []));
        mExcludeEventTypeFilter.set(this, isNull(excludeEventTypeFilter, []));
        mIncludeStatusFilter.set(this, isNull(includeStatusFilter, []));
        mExcludeStatusFilter.set(this, isNull(excludeStatusFilter, []));
        mEventTrackerFilter.set(this, isNull(eventTrackerFilter, []));
        mEventHasNoTrackersFilter.set(this, isNull(eventHasNoTrackersFilter, false));
        mTargetFilter.set(this, isNull(targetFilter, []));
        mTargetFilterRequireAll.set(this, isNull(targetFilterRequireAll, false));
        mLocationStateFilter.set(this, isNull(locationStateFilter, []));
        mOfficeStateFilter.set(this, isNull(officeStateFilter, []));
        mOfficeTypeFilter.set(this, isNull(officeTypeFilter, []));
        this.rangeStart = readRangeParamFromStr(eventRangeStart);
        this.rangeEnd = readRangeParamFromStr(eventRangeEnd);
        this.reportedRangeStart = readRangeParamFromStr(eventReportedRangeStart);
        this.reportedRangeEnd = readRangeParamFromStr(eventReportedRangeEnd);
    }

    toString() {
        let filterData = `{ EventFilter: ${this.toJSON()} }`;
        return filterData;
    }

    get nameFilter() {
        return mEventNameFilter.get(this);
    }

    set nameFilter(updatedNameFilter) {
        mEventNameFilter.set(this, updatedNameFilter);
    }

    get includeTypeFilter() {
        return mIncludeEventTypeFilter.get(this);
    }

    set includeTypeFilter(updatedTypeFilter) {
        mIncludeEventTypeFilter.set(this, updatedTypeFilter);
    }

    get excludeTypeFilter() {
        return mExcludeEventTypeFilter.get(this);
    }

    set excludeTypeFilter(updatedTypeFilter) {
        mExcludeEventTypeFilter.set(this, updatedTypeFilter);
    }

    get includeStatusFilter() {
        return mIncludeStatusFilter.get(this);
    }

    set includeStatusFilter(updatedVal) {
        mIncludeStatusFilter.set(this, updatedVal);
    }

    get excludeStatusFilter() {
        return mExcludeStatusFilter.get(this);
    }

    set excludeStatusFilter(updatedVal) {
        mExcludeStatusFilter.set(this, updatedVal);
    }

    get trackerFilter() {
        return mEventTrackerFilter.get(this);
    }

    set trackerFilter(updatedVal) {
        mEventTrackerFilter.set(this, updatedVal);
    }

    get hasNoTrackersFilter() {
        return mEventHasNoTrackersFilter.get(this);
    }

    set hasNoTrackersFilter(updatedVal) {
        mEventHasNoTrackersFilter.set(this, updatedVal);
    }

    get targetFilter() {
        return mTargetFilter.get(this);
    }

    set targetFilter(updatedVal) {
        mTargetFilter.set(this, updatedVal);
    }

    get targetFilterRequireAll() {
        return mTargetFilterRequireAll.get(this);
    }

    set targetFilterRequireAll(updatedVal) {
        mTargetFilterRequireAll.set(this, updatedVal === true);
    }

    get locationStateFilter() {
        return mLocationStateFilter.get(this);
    }

    set locationStateFilter(updatedVal) {
        mLocationStateFilter.set(this, updatedVal);
    }

    get officeStateFilter() {
        return mOfficeStateFilter.get(this);
    }

    set officeStateFilter(updatedVal) {
        mOfficeStateFilter.set(this, updatedVal);
    }

    get officeTypeFilter() {
        return mOfficeTypeFilter.get(this);
    }

    set officeTypeFilter(updatedVal) {
        mOfficeTypeFilter.set(this, updatedVal);
    }

    get rangeStart() {
        return mEventRangeStart.get(this);
    }

    set rangeStart(updatedVal) {
        if (moment.isMoment(updatedVal) && (updatedVal.isValid())) {
            mEventRangeStart.set(this, updatedVal);
        }
        else {
            mEventRangeStart.set(this, null);
        }
    }

    get hasRangeStart() {
        return (this.rangeStart != null);
    }

    get rangeEnd() {
        return mEventRangeEnd.get(this);
    }

    set rangeEnd(updatedVal) {
        if (moment.isMoment(updatedVal) && (updatedVal.isValid())) {
            mEventRangeEnd.set(this, updatedVal);
        }
        else {
            mEventRangeEnd.set(this, null);
        }
    }

    get hasRangeEnd() {
        return (this.rangeEnd != null);
    }

    get reportedRangeStart() {
        return mEventReportedRangeStart.get(this);
    }

    set reportedRangeStart(updatedVal) {
        if (moment.isMoment(updatedVal) && (updatedVal.isValid())) {
            mEventReportedRangeStart.set(this, updatedVal);
        }
        else {
            mEventReportedRangeStart.set(this, null);
        }
    }

    get hasReportedRangeStart() {
        return (this.reportedRangeStart != null);
    }

    get reportedRangeEnd() {
        return mEventReportedRangeEnd.get(this);
    }

    set reportedRangeEnd(updatedVal) {
        if (moment.isMoment(updatedVal) && (updatedVal.isValid())) {
            mEventReportedRangeEnd.set(this, updatedVal);
        }
        else {
            mEventReportedRangeEnd.set(this, null);
        }
    }

    get hasReportedRangeEnd() {
        return (this.reportedRangeEnd != null);
    }

    toJSON() {
        let bareFilters = {
            eventNameFilter: this.nameFilter,
            includeTypeFilter: this.includeTypeFilter,
            excludeTypeFilter: this.excludeTypeFilter,
            includeStatusFilter: this.includeStatusFilter,
            excludeStatusFilter: this.excludeStatusFilter,
            eventTrackerFilter: this.trackerFilter,
            eventHasNoTrackersFilter: this.hasNoTrackersFilter,
            targetFilter: this.targetFilter,
            targetFilterRequireAll: this.targetFilterRequireAll,
            locationStateFilter: this.locationStateFilter,
            officeStateFilter: this.officeStateFilter,
            officeTypeFilter: this.officeTypeFilter,
            eventRangeStart: (this.rangeStart != null ? this.rangeStart.toISOString() : null),
            eventRangeEnd: (this.rangeEnd != null ? this.rangeEnd.toISOString() : null),
            eventReportedRangeStart: (this.reportedRangeStart != null ? this.reportedRangeStart.toISOString() : null),
            eventReportedRangeEnd: (this.reportedRangeEnd != null ? this.reportedRangeEnd.toISOString() : null),
        };

        return JSON.stringify(bareFilters);
    }

    _getRawRangeFilters() {
        return [
            this._getRawEventDateRangeFilter(), this._getRawEventReportedRangeFilter()
        ].filter((rf) => rf != null);
    }

    _buildRawRangeFilter(rangeFilterSpec) {
        /* {
            start: {
                fieldName: "", //field
                paramTest: () => true, //function testing if the parameter exists
                paramGetter: () => new moment() // function that returns the stored field value (moment)
            },
            end: {
                // same spec as start
            }
        }; */

        let rangeFilters = [];

        if (rangeFilterSpec.start.paramTest()) {
            rangeFilters.push({
                field: rangeFilterSpec.start.fieldName,
                operator: "gte",
                value: rangeFilterSpec.start.paramGetter().toISOString()
            });
        }

        if (rangeFilterSpec.end.paramTest()) {
            rangeFilters.push({
                field: rangeFilterSpec.end.fieldName,
                operator: "lte",
                value: rangeFilterSpec.end.paramGetter().toISOString()
            });
        }

        let rawRangeFilter = null;

        if (rangeFilters.length > 0) {
            rawRangeFilter = {
                logic: "and",
                filters: rangeFilters
            };
        }

        return rawRangeFilter;
    }

    _getRawEventDateRangeFilter() {
        return this._buildRawRangeFilter({
            start: {
                paramTest: () => this.hasRangeStart,
                fieldName: EventFilterNames.rangeStartFilter,
                paramGetter: () => this.rangeStart
            },
            end: {
                paramTest: () => this.hasRangeEnd,
                fieldName: EventFilterNames.rangeEndFilter,
                paramGetter: () => this.rangeEnd
            }
        });
    }

    _getRawEventReportedRangeFilter() {
        return this._buildRawRangeFilter({
            start: {
                paramTest: () => this.hasReportedRangeStart,
                fieldName: EventFilterNames.reportedRangeStartFilter,
                paramGetter: () => this.reportedRangeStart
            },
            end: {
                paramTest: () => this.hasReportedRangeEnd,
                fieldName: EventFilterNames.reportedRangeEndFilter,
                paramGetter: () => this.reportedRangeEnd
            }
        });
    }

    isEmpty() {
        const activeFilters = [
            (() => {
                return (this.nameFilter != null && this.nameFilter.length > 0) ? [ true ] : [];
            })(),
            this.includeTypeFilter,
            this.excludeTypeFilter,
            this.includeStatusFilter,
            this.excludeStatusFilter,
            this.trackerFilter,
            (() => {
                return this.hasNoTrackersFilter ? [ true ] : [];
            })(),
            this.targetFilter,
            this.locationStateFilter,
            this.officeStateFilter,
            this.officeTypeFilter,
            (() => {
                return [this.rangeStart, this.rangeEnd].filter((r) => (r != null));
            })(),
            (() => {
                return [this.reportedRangeStart, this.reportedRangeEnd].filter((r) => (r != null));
            })()
        ]
            .filter((f) => (f.length > 0));

        return (activeFilters.length === 0);
    }

    /* Translates this event filter into a kendo data source filter object.
     * Any changes to the event filter itself must be made to a copy*/
    dataSourceFilter(globalViewRuleFn) {
        const applyGlobalViewRule = (currEventFilter, ruleFn) => {
            if (ruleFn === null || ruleFn === undefined) {
                return currEventFilter;
            }
            const evtFilterClone = EventFilter.copyFilter(null, currEventFilter);
            return ruleFn(evtFilterClone);
        };

        const currentEventFilter = applyGlobalViewRule(this, globalViewRuleFn);

        let trackingEventFilter = {
            logic: "and",
            rawFilters: [
                {
                    logic: "or",
                    filters:
                        currentEventFilter.includeStatusFilter.map(
                            (tStatusId) => buildObjForIdParam(tStatusId, EventFilterNames.includeStatusFilter)
                            )
                            .filter(tStatusIdFilter => (tStatusIdFilter != null))
                },
                {
                    logic: "or",
                    filters:
                        currentEventFilter.includeTypeFilter.map(
                            (evtTypeId) => buildObjForIdParam(evtTypeId, EventFilterNames.includeTypeFilter)
                            )
                            .filter(evtTypeIdFilter => (evtTypeIdFilter != null))
                },
                {
                    logic: "or",
                    filters:
                        currentEventFilter.locationStateFilter.map(
                            (locStateShortName) => {
                                return {
                                    field: EventFilterNames.locationStateFilter,
                                    operator: "eq",
                                    value: locStateShortName
                                };
                            })
                },
                {
                    logic: (() => {
                        return currentEventFilter.targetFilterRequireAll ? "and" : "or";
                    })(),
                    filters:
                        currentEventFilter.targetFilter.map(
                            (tgtIdStr) => buildObjForIdParam(tgtIdStr, EventFilterNames.targetFilter)
                            )
                            .filter((tgtFilterEntry) => (tgtFilterEntry != null))
                },
                {
                    logic: "or",
                    filters:
                        currentEventFilter.officeStateFilter.map(
                            (offStateShortName) => {
                                return {
                                    field: EventFilterNames.officeStateFilter,
                                    operator: "eq",
                                    value: offStateShortName
                                };
                            })
                },
                {
                    logic: "or",
                    filters:
                        currentEventFilter.officeTypeFilter.map(
                            (offTypeId) => buildObjForIdParam(offTypeId, EventFilterNames.officeTypeFilter)
                            )
                            .filter((offTypeFilter) => (offTypeFilter != null))
                }
            ]
        };

        let eventRangeRawFilters = currentEventFilter._getRawRangeFilters();

        if (eventRangeRawFilters != null) {
            Array.prototype.push.apply(trackingEventFilter.rawFilters, eventRangeRawFilters);
        }

        if (currentEventFilter.hasNoTrackersFilter === true) {
            trackingEventFilter.rawFilters.push({
                logic: "or", //doesn't actually matter.
                filters: [{
                    field: EventFilterNames.hasNoTrackersFilter,
                    operator: "eq",
                    value: !currentEventFilter.hasNoTrackersFilter
                }]
            });
        }
        else {
            trackingEventFilter.rawFilters.push({
                logic: "or",
                filters:
                    currentEventFilter.trackerFilter.map(
                        (eventTrackerId) => {
                            return {
                                field: EventFilterNames.trackerFilter,
                                operator: "eq",
                                value: eventTrackerId
                            };
                        })
            });
        }

        let currentEventNameFilter = currentEventFilter.nameFilter;
        if ((currentEventNameFilter != null) &&
            (angular.isString(currentEventNameFilter)) &&
            (currentEventNameFilter.length > 0))
        {
            trackingEventFilter.rawFilters.push({
                logic: "or",
                filters: [{
                    field: EventFilterNames.nameFilter,
                    operator: "contains",
                    value: currentEventNameFilter
                }]
            });
        }

        trackingEventFilter.filters =
            trackingEventFilter.rawFilters
                .filter((filterEntry) => (filterEntry.filters.length > 0))
                .map(normalizeFilterEntry);

        const handleExcludeFilter = (getFilterFn, eventFilterName) => {
            if (getFilterFn(currentEventFilter).length > 0) {

                const rawExcludeItemFilter = [{
                    logic: "or",
                    filters: getFilterFn(currentEventFilter)
                        .map((itemId) => buildObjForIdParam(itemId, eventFilterName))
                        .filter(exFilter => (exFilter != null))
                }];

                const normalizedExcludeItemFilter = {
                    logic: "not",
                    filters: rawExcludeItemFilter.map(normalizeFilterEntry)
                };

                trackingEventFilter.filters.push(normalizedExcludeItemFilter);
            }
        };

        handleExcludeFilter(
            (evtFilter) => evtFilter.excludeStatusFilter,
            EventFilterNames.excludeStatusFilter);
        handleExcludeFilter(
            (evtFilter) => evtFilter.excludeTypeFilter,
            EventFilterNames.excludeTypeFilter);

        delete trackingEventFilter.rawFilters;

        if (trackingEventFilter.filters.length === 0) {
            trackingEventFilter = null;
        }

        return trackingEventFilter;
    }

    static fromJSON(filterJson) {
        if (typeof filterJson !== "string") {
            throw new TypeError("Event Filter: Cannot Load JSON From Non-String");
        }

        if (filterJson.length === 0) {
            return new EventFilter({});
        }

        let filterData = JSON.parse(filterJson);

        return new EventFilter(filterData);
    }

    static copyFilter(targetFilter, sourceFilter) {
        let copySource = (sourceFilter != null) ? sourceFilter : new EventFilter({}); //Effectively initialize a new one and then copy it. Maybe should just throw?
        let copyTarget = (targetFilter != null) ? targetFilter : new EventFilter({});

        //We are not building a new object here. We are making the target filters look like the source's filters without wiping away data structure.
        copyTarget.nameFilter = copySource.nameFilter;
        clearAndReplaceArray(copySource.includeTypeFilter, copyTarget.includeTypeFilter);
        clearAndReplaceArray(copySource.excludeTypeFilter, copyTarget.excludeTypeFilter);
        clearAndReplaceArray(copySource.includeStatusFilter, copyTarget.includeStatusFilter);
        clearAndReplaceArray(copySource.excludeStatusFilter, copyTarget.excludeStatusFilter);
        clearAndReplaceArray(copySource.trackerFilter, copyTarget.trackerFilter);
        copyTarget.hasNoTrackersFilter = copySource.hasNoTrackersFilter;
        clearAndReplaceArray(copySource.targetFilter, copyTarget.targetFilter);
        copyTarget.targetFilterRequireAll = copySource.targetFilterRequireAll;
        clearAndReplaceArray(copySource.locationStateFilter, copyTarget.locationStateFilter);
        clearAndReplaceArray(copySource.officeStateFilter, copyTarget.officeStateFilter);
        clearAndReplaceArray(copySource.officeTypeFilter, copyTarget.officeTypeFilter);
        copyTarget.rangeStart = copySource.hasRangeStart ? copySource.rangeStart.clone() : null;
        copyTarget.rangeEnd = copySource.hasRangeEnd ? copySource.rangeEnd.clone() : null;
        copyTarget.reportedRangeStart = copySource.hasReportedRangeStart ? copySource.reportedRangeStart.clone() : null;
        copyTarget.reportedRangeEnd = copySource.hasReportedRangeEnd ? copySource.reportedRangeEnd.clone() : null;

        return copyTarget;
    }
}

makePropsEnumerable(EventFilter.prototype, [
    "nameFilter",
    "includeTypeFilter",
    "excludeTypeFilter",
    "includeStatusFilter",
    "excludeStatusFilter",
    "trackerFilter",
    "hasNoTrackersFilter",
    "targetFilter",
    "targetFilterRequireAll",
    "locationStateFilter",
    "officeStateFilter",
    "officeTypeFilter",
    "rangeStart",
    "rangeEnd",
    "reportedRangeStart",
    "reportedRangeEnd",
    "toJSON",
    "dataSourceFilter"
]);

export default EventFilter;



//////////////////
// WEBPACK FOOTER
// ./src/models/EventFilter.js
// module id = m46M
// module chunks = 1